import {AceLoggerService} from '@logging/ace-logger.service';
import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import {Contractor} from '../api/contractor-interfaces';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {forkJoin, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {PagedModel} from '@shared/api/model/paged-model';
import {BaseUserService} from '@shared/services/api/base-user.service';
import {SiteToUserIdByUserEntityIdMapping} from '@app/graphql-typings/graphql';
import {User} from '../api/user-interfaces';
import {UserService} from '../users/user.service';
import {AceLogger} from '@logging/ace-logger';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import {HttpUtilityService} from '@shared/services/api/http-utility-service';
import {APIContractorEntity, ContractorId} from "@shared/services/api/user-entity";


interface ContractorAPICreate {
  contractor_id: ContractorId;
}


interface ContractorAPIResponse {
  contractor: APIContractorEntity;
}

interface ContractorsAPIResponse {
  contractors: APIContractorEntity[];
}

export const contractorResolver: ResolveFn<Contractor> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<Contractor> => {
  return inject(ContractorService).getContractor(+route.paramMap.get('id'));
};

@Injectable({
  providedIn: 'root'
})
export class ContractorService extends BaseUserService {

  private headers: HttpHeaders = this.getHeaders();
  private readonly logger: AceLogger;
  private readonly CONTRACTOR_ID_KEY: string = 'contractor_id';
  private readonly CONTRACTOR_KEY: string = 'contractor';
  private readonly CONTRACTOR_KEY_PLURAL: string = this.CONTRACTOR_KEY+'s';

  private static map_contractor_from_api(contractor: APIContractorEntity): Contractor {
    return new Contractor(contractor.contractor_id, contractor.contractor_name, (contractor.logo_url ? new URL(contractor.logo_url) : null), contractor.allowed_actions);
  }

  private static map_contractor_to_api(contractor: Contractor) {
    return {
      contractor_name: contractor.name,
      ...(contractor.id !== null && {contractor_id: contractor.id})
    };
  }

  constructor(private http: HttpClient,
              private userService: UserService,
              private httpUtilityService: HttpUtilityService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(ContractorService.name);
  }

  getContractor(contractorId: ContractorId): Observable<Contractor> {
    const options = {headers: this.headers};
    return this.http.get(this.getGateway() + '/users/contractors/' + contractorId, options)
      .pipe(map((response: APIResponse<ContractorAPIResponse>) =>
        ContractorService.map_contractor_from_api(this.httpUtilityService.extractResponseData(response, this.CONTRACTOR_KEY))));
  }

  getContractors(first: number, max: number, searchName?: string | null): Observable<PagedModel<Contractor[]>> {
    let params = new HttpParams().set('first', first.toString()).set('max', max.toString());
    const searchNameTrimmed = searchName?.trim();
    if (searchNameTrimmed) {
      params = params.append('contractor_name', '%' + searchNameTrimmed + '%');
    }
    const options = {headers: this.headers, params: params};

    return this.http.get(this.getGateway() + '/users/contractors', options)
      .pipe(map((response: APIResponse<ContractorsAPIResponse>) => {
        const contractorsResult: PagedModel<APIContractorEntity[]> = this.httpUtilityService.extractPagedResponse(response, this.CONTRACTOR_KEY_PLURAL);
        return {
          data: contractorsResult.data.map(ContractorService.map_contractor_from_api),
          count: contractorsResult.count,
        };
      }));
  }

  createContractor(contractor: Contractor): Observable<ContractorId> {
    const options = {headers: this.headers};
    return this.http.post(this.getGateway() + '/users/contractors', ContractorService.map_contractor_to_api(contractor), options)
      .pipe(map((response: APIResponse<ContractorAPICreate>) => this.httpUtilityService.extractResponseData(response, this.CONTRACTOR_ID_KEY)));
  }

  updateContractor(contractor: Contractor): Observable<Object> {
    const options = {headers: this.headers};
    return this.http.put(this.getGateway() + '/users/contractors/' + contractor.id, ContractorService.map_contractor_to_api(contractor), options);
  }

  updateContractorLogo(contractorId: ContractorId, logo: { data: string, content_type: string }): Observable<Object> {
    const options = {headers: this.headers};
    return this.http.post(this.getGateway() + '/users/contractors/' + contractorId + '/logo', logo, options);
  }

  deleteContractorLogo(contractorId: ContractorId): Observable<Object> {
    const options = {headers: this.headers};
    return this.http.delete(this.getGateway() + '/users/contractors/' + contractorId + '/logo', options);
  }

  public initSiteContractorMapping(siteContractorMappings: SiteToUserIdByUserEntityIdMapping):
    Observable<{ contractors: Contractor[], selectedContractorUsers: Map<number, User[]>, contractorUsers: Map<number, User[]> }> {
    const contractors = siteContractorMappings ? Object.keys(siteContractorMappings) : null;
    if (contractors && contractors.length) {
      const m = new Map<number, Set<string>>();
      contractors.forEach((contractorId: string) => m.set(+contractorId, new Set(siteContractorMappings[contractorId])));

      const selectedContractors: Contractor[] = [];
      const contractorUsers = new Map<number, User[]>();
      const contractorSelectedUsers = new Map<number, User[]>();
      const contractorObservables = [];
      const contractorUsersObservables = [];
      m.forEach((userIds: Set<string>, contractorId: ContractorId) => {
        contractorObservables.push(this.getContractor(contractorId));
        contractorUsersObservables.push(this.userService.getContractorUsers(contractorId, 0, 10000));
      });

      return forkJoin([
          forkJoin(contractorObservables),
          forkJoin(contractorUsersObservables)
      ]).pipe(
          map(result => {
            const contractorsResult = result[0];
            const contractorUsersResult = result[1];
            if (contractorsResult && contractorsResult.length) {
              contractorsResult.forEach((contractor: Contractor) => selectedContractors.push(contractor));
            }
            if (contractorUsersResult && contractorUsersResult.length) {
              contractorUsersResult.forEach((users: User[], index: number) => {
                const contractorId: ContractorId = selectedContractors[index].id;
                const selectedUserIds: Set<string> = m.get(contractorId);
                const selectedUsers: User[] = [];
                contractorUsers.set(contractorId, users.map(user => {
                  user['name'] = user.firstname + ' ' + user.lastname;
                  if (selectedUserIds.has(user.id)) {
                    selectedUsers.push(user);
                  }
                  return user;
                }));
                contractorSelectedUsers.set(contractorId, selectedUsers);
              });
            }
            return {
              contractors: selectedContractors.sort((a, b) => a.name.localeCompare(b.name)),
              selectedContractorUsers: contractorSelectedUsers,
              contractorUsers: contractorUsers
            };
          }));
    } else {
      return of({ contractors: [], selectedContractorUsers: new Map(), contractorUsers: new Map() });
    }
  }

}
